import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Label, TextInput, Alert, Checkbox } from 'flowbite-react';
import { staffApi, type StaffAssignmentCreateData, type StaffAssignment } from '../../api/staff';
import { type Descriptor } from '../../api/descriptors';
import { dateInputBounds, validateDateFields } from '../../utils/dateValidation';
import { useRoleTagSuggestions } from '../../hooks/useRoleTagSuggestions';
import { RoleTagPicker, OTHER_ROLE_CODE } from '../staff/RoleTagPicker';
import { SearchableSelect } from '../common/SearchableSelect';

/**
 * Sort role descriptors alphabetically by shortDescription
 */
const sortRoleDescriptorsAlphabetically = (descriptors: Descriptor[]): Descriptor[] => {
  return [...descriptors].sort((a, b) =>
    (a.shortDescription || '').localeCompare(b.shortDescription || '')
  );
};

/**
 * Sort role descriptors: School Board items first, then alphabetically by shortDescription
 */
const sortRoleDescriptorsBoardFirst = (descriptors: Descriptor[]): Descriptor[] => {
  return [...descriptors].sort((a, b) => {
    // Check if either is a School Board role (by codeValue prefix or shortDescription)
    const aIsBoard = a.codeValue?.startsWith('Board') || a.shortDescription?.toLowerCase().includes('school board');
    const bIsBoard = b.codeValue?.startsWith('Board') || b.shortDescription?.toLowerCase().includes('school board');

    // School Board items come first
    if (aIsBoard && !bIsBoard) return -1;
    if (!aIsBoard && bIsBoard) return 1;

    // Within same category, sort alphabetically by shortDescription
    return (a.shortDescription || '').localeCompare(b.shortDescription || '');
  });
};

interface AddAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  // For positions, data.roleTagDescriptorIds is the full set of tags the
  // position's organization should have; the server saves them with the
  // position in one request.
  onSave: (data: StaffAssignmentCreateData) => Promise<void>;
  assignment?: StaffAssignment | null;
  organizations: Array<{ id: number; name: string }>;
  roleDescriptors: Descriptor[];

  // Current role tag rows at the edited position's organization
  existingRoleTags?: StaffAssignment[];
  // Roles that already apply from elsewhere (tags saved at a school's
  // district); shown locked, never saved here
  inheritedRoleIds?: number[];
  // How many current positions the person has at this organization; tags
  // there are shared by all of them
  sharedPositionCount?: number;

  // Optional: Lock organization selection to a specific org (for org profile pages)
  lockedOrganizationId?: number;
  // Name to show for the locked org when it isn't in `organizations` (e.g. a school)
  lockedOrganizationName?: string;

  // Optional: Default values for new assignments
  defaultAssignmentType?: 'primary' | 'roleTag';
  defaultPositionTitle?: string;
  defaultRoleDescriptorId?: number;

  // Optional: Control auto-close behavior after successful save
  autoCloseOnSave?: boolean;

  // Optional: Prioritize School Board roles at top of dropdown (for Board tab context)
  prioritizeBoardRoles?: boolean;
}

export const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  assignment,
  organizations,
  roleDescriptors,
  existingRoleTags = [],
  inheritedRoleIds = [],
  sharedPositionCount = 0,
  lockedOrganizationId,
  lockedOrganizationName,
  defaultAssignmentType = 'primary',
  defaultPositionTitle,
  defaultRoleDescriptorId,
  autoCloseOnSave = true,
  prioritizeBoardRoles = false
}) => {
  const [assignmentType, setAssignmentType] = useState<'primary' | 'roleTag'>(defaultAssignmentType);
  const [formData, setFormData] = useState<StaffAssignmentCreateData>({
    educationOrganizationId: lockedOrganizationId || 0,
    positionTitle: defaultPositionTitle || '',
    roleDescriptorId: defaultRoleDescriptorId,
    beginDate: undefined,
    endDate: undefined,
    isPrimary: defaultAssignmentType === 'primary',
    includeInDirectory: defaultAssignmentType === 'primary'
  });
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  // Once the editor picks tags themselves, stop replacing them with suggestions
  const [tagsTouched, setTagsTouched] = useState(false);
  const [tagsInvalid, setTagsInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort role descriptors: alphabetically by default, or with Board roles first if in Board context
  const sortedRoleDescriptors = useMemo(
    () => prioritizeBoardRoles
      ? sortRoleDescriptorsBoardFirst(roleDescriptors)
      : sortRoleDescriptorsAlphabetically(roleDescriptors),
    [roleDescriptors, prioritizeBoardRoles]
  );

  const isPosition = assignmentType === 'primary';
  const { suggestions, loading: loadingSuggestions, resolvedTitle } = useRoleTagSuggestions(
    formData.positionTitle,
    formData.educationOrganizationId || undefined,
    isOpen && isPosition
  );

  // A role on the position row itself (board roles) or a tag saved at the
  // school's district already makes the contact findable, so both count as
  // tags. They show locked and aren't saved from here.
  const lockedRoleIds = isPosition
    ? [...(formData.roleDescriptorId ? [formData.roleDescriptorId] : []), ...inheritedRoleIds]
    : [];
  const otherDescriptor = roleDescriptors.find(d => d.codeValue === OTHER_ROLE_CODE);

  // An ended position's tags ended with it and come back when its end date
  // is cleared, so they aren't edited (or required) here. The tags shown for
  // its organization would belong to the person's other positions.
  const isEndedPosition = !!assignment && !!assignment.endDate && (() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return assignment.endDate!.slice(0, 10) <= todayStr;
  })();
  const editsTags = isPosition && !isEndedPosition;
  const tagRequired = editsTags && !!formData.includeInDirectory && lockedRoleIds.length === 0 && !!otherDescriptor;

  // An existing assignment can't move organizations, so editing always shows
  // its own organization read-only (the district Staff tab locks to the
  // district even when the position is at one of its schools).
  const orgIsFixed = !!assignment || !!lockedOrganizationId;
  const lockedOrgName = assignment
    ? assignment.organizationName
    : lockedOrganizationId
      ? lockedOrganizationName || organizations.find(org => org.id === lockedOrganizationId)?.name
      : undefined;

  useEffect(() => {
    if (isOpen) {
      if (assignment) {
        // Editing existing assignment - pre-populate form
        const isRoleTag = !assignment.isPrimary && !assignment.includeInDirectory && assignment.roleDescriptorId;
        setAssignmentType(isRoleTag ? 'roleTag' : 'primary');
        setFormData({
          educationOrganizationId: assignment.educationOrganizationId,
          positionTitle: assignment.positionTitle || '',
          roleDescriptorId: assignment.roleDescriptorId || undefined,
          beginDate: assignment.beginDate || undefined,
          endDate: assignment.endDate || undefined,
          isPrimary: assignment.isPrimary,
          includeInDirectory: assignment.includeInDirectory
        });
        setSelectedTagIds(Array.from(new Set(
          existingRoleTags
            .map(t => t.roleDescriptorId)
            .filter((id): id is number => !!id)
        )));
        setTagsTouched(true);
      } else {
        // Adding new assignment - reset form with defaults
        setAssignmentType(defaultAssignmentType);
        setFormData({
          educationOrganizationId: lockedOrganizationId || organizations[0]?.id || 0,
          positionTitle: defaultPositionTitle || (defaultAssignmentType === 'primary' ? '' : undefined),
          roleDescriptorId: defaultRoleDescriptorId,
          beginDate: undefined,
          endDate: undefined,
          isPrimary: defaultAssignmentType === 'primary',
          includeInDirectory: defaultAssignmentType === 'primary'
        });
        setSelectedTagIds([]);
        setTagsTouched(false);
      }
      setTagsInvalid(false);
      setError(null);
    }
  }, [isOpen, assignment, organizations, lockedOrganizationId, defaultAssignmentType, defaultPositionTitle, defaultRoleDescriptorId]);

  // New positions: keep the tags in step with the title until the editor
  // changes them by hand.
  useEffect(() => {
    if (!isOpen || assignment || !isPosition || tagsTouched) return;
    setSelectedTagIds(
      suggestions
        .map(s => s.descriptorId)
        .filter(id => id !== formData.roleDescriptorId)
    );
  }, [suggestions]);

  const handleTagsChange = (ids: number[]) => {
    setTagsTouched(true);
    setTagsInvalid(false);
    setSelectedTagIds(ids);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.educationOrganizationId) {
      setError('Please select an organization');
      return;
    }

    if (isPosition && !formData.positionTitle) {
      setError('Position title is required for primary assignments');
      return;
    }

    if (assignmentType === 'roleTag' && !formData.roleDescriptorId) {
      setError('Please select a role for this tag');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.beginDate, label: 'Start Date' },
      { value: formData.endDate, label: 'End Date' },
    ]);
    if (dateError) {
      setError(dateError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // A new position's untouched tags follow its title. If the title
      // changed after the last suggestions arrived, fetch fresh ones so the
      // tags saved match the title saved.
      let chosenIds = selectedTagIds;
      const title = (formData.positionTitle || '').trim();
      if (isPosition && !assignment && !tagsTouched && title !== resolvedTitle) {
        try {
          const fresh = title.length >= 3
            ? await staffApi.getRoleTagSuggestions(title, formData.educationOrganizationId || undefined)
            : [];
          chosenIds = fresh.map(s => s.descriptorId);
          setSelectedTagIds(chosenIds);
        } catch (suggestErr) {
          // Keep the tags already shown rather than blocking the save
          console.error('Failed to refresh role tag suggestions before saving:', suggestErr);
        }
      }
      // The position's own role is kept by the server. An inherited (district)
      // tag is only left out when there's no copy at this organization too.
      const existingIds = existingRoleTags.map(t => t.roleDescriptorId);
      const tagIds = editsTags
        ? chosenIds.filter(id =>
            id !== formData.roleDescriptorId &&
            (!inheritedRoleIds.includes(id) || existingIds.includes(id))
          )
        : [];

      if (tagRequired && tagIds.length === 0) {
        setTagsInvalid(true);
        setError('Add at least one role tag so this contact shows up in the Role filter. If none fit, choose "None of these fit".');
        return;
      }

      await onSave(editsTags ? { ...formData, roleTagDescriptorIds: tagIds } : formData);

      // Only close if autoCloseOnSave is true
      if (autoCloseOnSave) {
        handleClose();
      } else {
        // Reset form for next assignment (keep modal open)
        setAssignmentType('roleTag'); // Switch to role tag for next assignment
        setFormData({
          educationOrganizationId: lockedOrganizationId || formData.educationOrganizationId,
          positionTitle: undefined,
          roleDescriptorId: undefined,
          beginDate: undefined,
          endDate: undefined,
          isPrimary: false,
          includeInDirectory: false
        });
        setSelectedTagIds([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      educationOrganizationId: lockedOrganizationId || 0,
      positionTitle: '',
      roleDescriptorId: undefined,
      beginDate: undefined,
      endDate: undefined,
      isPrimary: false,
      includeInDirectory: true
    });
    setSelectedTagIds([]);
    setTagsTouched(false);
    setTagsInvalid(false);
    setError(null);
    onClose();
  };

  const heading = isPosition
    ? (assignment ? 'Edit Assignment' : 'Add Assignment')
    : (assignment ? 'Edit Role Tag' : 'Add Role Tag');

  return (
    <Modal show={isOpen} onClose={handleClose} dismissible popup size="lg">
      <div className="p-6">
        <h3 className="text-xl font-medium text-gray-900 mb-4">
          {heading}
        </h3>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization Selection (or locked display) */}
          <div>
            <Label htmlFor="organization" className="mb-2">
              Organization
            </Label>
            {orgIsFixed ? (
              <div className="p-2.5 bg-gray-100 border border-gray-300 rounded-lg">
                <p className="text-gray-700 font-medium">{lockedOrgName}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Organization is locked for this assignment
                </p>
              </div>
            ) : (
              <SearchableSelect
                id="organization"
                options={organizations.map(org => ({ value: org.id, label: org.name }))}
                value={formData.educationOrganizationId || null}
                onChange={(orgId) => setFormData({
                  ...formData,
                  educationOrganizationId: orgId ?? 0
                })}
                placeholder="Search organizations..."
              />
            )}
          </div>

          {/* Position Title + its role tags */}
          {isPosition && (
            <>
              <div>
                <Label htmlFor="positionTitle" className="mb-2">
                  Position Title
                </Label>
                <TextInput
                  id="positionTitle"
                  placeholder="e.g., Superintendent, Director of Technology"
                  value={formData.positionTitle || ''}
                  onChange={(e) => setFormData({ ...formData, positionTitle: e.target.value })}
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  The organization's own title, as it should appear in the directory
                </p>
              </div>

              {isEndedPosition ? (
                <p className="text-xs text-gray-500">
                  This position has ended. Its role tags ended with it and come back if you clear the end date.
                </p>
              ) : (
                <RoleTagPicker
                  roleDescriptors={sortedRoleDescriptors}
                  selectedIds={selectedTagIds}
                  onChange={handleTagsChange}
                  suggestions={suggestions}
                  loadingSuggestions={loadingSuggestions}
                  lockedIds={lockedRoleIds}
                  invalid={tagsInvalid}
                  disabled={loading}
                />
              )}
              {editsTags && sharedPositionCount > 1 && (
                <p className="-mt-2 text-xs text-gray-500">
                  These tags apply to all {sharedPositionCount} of this person's positions at {lockedOrgName || 'this organization'}.
                </p>
              )}
            </>
          )}

          {/* Single role tag (tags not attached to a position) */}
          {assignmentType === 'roleTag' && (
            <div>
              <Label htmlFor="role" className="mb-2">
                Standard Role
              </Label>
              <SearchableSelect
                id="role"
                options={sortedRoleDescriptors.map(role => ({ value: role.descriptorId, label: role.shortDescription }))}
                value={formData.roleDescriptorId ?? null}
                onChange={(roleId) => setFormData({
                  ...formData,
                  roleDescriptorId: roleId ?? undefined
                })}
                placeholder="Search roles..."
              />
              <p className="mt-1 text-sm text-gray-500">
                Lets people find this contact with the Role filter in Search Contacts
              </p>
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="beginDate" className="mb-2">
                Start Date
              </Label>
              <TextInput
                id="beginDate"
                type="date"
                value={formData.beginDate || ''}
                onChange={(e) => setFormData({ ...formData, beginDate: e.target.value })}
                {...dateInputBounds}
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="mb-2">
                End Date
              </Label>
              <TextInput
                id="endDate"
                type="date"
                value={formData.endDate || ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                {...dateInputBounds}
              />
            </div>
          </div>

          {/* Directory Inclusion (for primary assignments) */}
          {isPosition && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeInDirectory"
                checked={formData.includeInDirectory}
                onChange={(e) => setFormData({
                  ...formData,
                  includeInDirectory: e.target.checked
                })}
              />
              <Label htmlFor="includeInDirectory">
                Include in public directory
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? (assignment ? 'Updating...' : 'Adding...')
                : `${assignment ? 'Update' : 'Add'} ${isPosition ? 'Assignment' : 'Role Tag'}`
              }
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
